import type { MutateResult } from '@vue/apollo-composable';
import type { CheckoutMutation_ManageCheckout_Checkout } from '~/@types/nestedGraphqlTypes';
import {
    CheckoutActionType,
    type CartDiscountCodesUpdateMutation,
    type CartDiscountCodesUpdateMutationVariables,
    type CheckoutFieldsFragment,
    type CheckoutMutation,
    type CheckoutMutationVariables,
    type ProductPreviewFieldsFragment,
} from '~/graphql/generated';

type CartStatus = 'init' | 'busy' | 'ready' | 'error';

export type CartItem = Omit<TSPath<CheckoutMutation_ManageCheckout_Checkout, ['lineItems']>[number], 'id' | 'quantity'> & {
    /**
     * id is fixed to be string (TODO: check provided type by API)
     */
    id: string;
    /**
     * quantity is fixed to be string (TODO: check provided type by API)
     */
    quantity: number;
    shopTitle: string;
    totalDiscount: number;
    quantityPrice: number;
    discountedPrice: number;
    priceString: string;
    compareAtPriceString?: string;
    attributes: Record<string, string | number>;
    type: 'Product';
    image?: string | null;
    maxQuantity: number;
};

interface CartMutationsInterfaceBase {
    processingStatus?: CartStatus;
    trackChanges?: boolean;
    componentOrigin?: string;
    customChangesHook?: (checkout: CheckoutFieldsFragment) => void;
}
type Callback = () => void;

const fallbackCurrency: Record<string, string> = {
    'en-us': 'USD',
    'de-ch': 'CHF',
    'fr-ch': 'CHF',
    default: 'EUR',
};

// https://pinia.vuejs.org/core-concepts/#setup-stores
export const useCartStore = defineStore('woom-store-cart', () => {
    const checkout = ref<CheckoutMutation_ManageCheckout_Checkout | null>(null);
    const cartItems = ref<CartItem[]>([]);
    const discountCodes = ref<string[]>([]);
    const cartStatus = ref<CartStatus>('init');
    const cartRecommendationData = ref<ProductPreviewFieldsFragment[]>([]);
    const initCallbacks: Callback[] = [];

    const nuxtApp = useNuxtApp();
    const authStore = useAuthStore();
    const { isoLocale, locale } = useLocales();

    /**
     * Static cart-recommendation from Shopify. To be replaced with smarter emarsys
     */
    const cartRecommendations = computed(() => {
        return cartRecommendationData.value.filter(
            (item) =>
                !cartItems.value.some((cartItem) => {
                    return cartItem?.variant?.productRef === item.id;
                }),
        );
    });

    const totalItems = computed(
        () => checkout.value?.lineItems?.reduce((previousValue, currentValue) => previousValue + (currentValue?.quantity ?? 1), 0) ?? 0,
    );

    function cartReadyHook(callback: Callback) {
        if (cartStatus.value !== 'init') return callback();
        initCallbacks.push(callback);
    }

    async function fetch() {
        authStore.checkToken(); // refreshes JWT from cookie
        const checkoutId = authStore.jwt?.cartId;
        if (!checkoutId) {
            cartStatus.value = 'ready';
            nuxtApp.$gtm.updateCart(null);
            return;
        }

        await mutationHandler({
            variables: {
                locale: locale.value,
                checkoutId: `${checkoutId}`,
                action: CheckoutActionType.Fetch,
            },
            processingStatus: 'init',
        });
    }

    function getComparePriceFormated() {
        const subtotal = checkout.value?.lineItemsSubtotalPrice?.amount || '0';
        const subtotal2 = checkout.value?.subtotalPrice?.amount || '0';
        if (subtotal === subtotal2) return;

        const currency = getCurrency();
        return new Intl.NumberFormat(isoLocale.value, { style: 'currency', currency }).format(Number.parseFloat(subtotal));
    }

    function getCurrency() {
        return checkout.value?.subtotalPrice?.currencyCode || fallbackCurrency[isoLocale.value] || fallbackCurrency.default;
    }

    /**
     * returns all line items that have changed with the new checkout
     */
    function getChanges(checkoutData?: CheckoutMutation_ManageCheckout_Checkout) {
        const lineItems = (checkoutData?.lineItems ?? []).filter(isDefined);
        const changes = {
            add: [] as NonNullable<CheckoutMutation_ManageCheckout_Checkout>['lineItems'],
            remove: [] as NonNullable<CheckoutMutation_ManageCheckout_Checkout>['lineItems'],
        };
        if (!checkout.value || !checkoutData) {
            changes.add = lineItems;
            return changes;
        }

        // console.log('existing checkout', checkout.value, checkout);
        for (const lineItem of lineItems) {
            // do we have this as existing line item
            const found = getLineitemById(lineItem.id as string);
            // console.log('found existing line item', found);
            if (!found) {
                // console.log('line item quantity increased', lineItem.quantity);
                changes.add.push(lineItem);
            } else if ((lineItem.quantity ?? 0) > (found.quantity ?? 0)) {
                // console.log('quantity was reduced', lineItem.quantity);
                changes.add.push(lineItem);
            } else if ((lineItem.quantity as number) < (found?.quantity ?? 0)) {
                // console.log('quantity was reduced', lineItem.quantity);
                changes.remove.push(lineItem);
            }
        }
        // now check for any removals
        for (const lineItem of checkout.value.lineItems) {
            const found = checkoutData.lineItems.find((p) => p.id === lineItem.id);
            if (found) continue;
            // console.log('found removed line item', lineItem);
            changes.remove.push(lineItem);
        }
        return changes;
    }

    function getLineitemById(id: string) {
        return checkout.value?.lineItems?.find((lineItem) => lineItem.id === id);
    }

    function getPriceFormated() {
        const subtotal2 = checkout.value?.subtotalPrice?.amount || '0';
        const currency = getCurrency();
        return new Intl.NumberFormat(isoLocale.value, { style: 'currency', currency }).format(Number.parseFloat(subtotal2));
    }

    function isLimitReached(productId: string, limit: number) {
        if (limit < 1) return false;
        let total = 0;
        for (const lineItem of checkout.value?.lineItems ?? []) {
            if (lineItem.variant?.productRef === productId) {
                total += lineItem.quantity ?? 0;
            }
        }
        return total >= limit;
    }

    async function loadCartCollection({ language, country }: { language: Ref<string>; country: Ref<string> }) {
        if (cartRecommendationData.value.length) return;

        try {
            const fetchOptions = useGenericFetchOptions();
            const data = await $fetch('/api/collection', {
                params: {
                    id: `${country.value}:${language.value}:collection:cart-recommendation`,
                },
                ...fetchOptions,
            });
            cartRecommendationData.value = (data || []) as ProductPreviewFieldsFragment[];
        } catch (error) {
            console.error('Error fetching Shopify cart-recommendation collection:', error);
        }
    }

    function isCheckoutMutation(mutationResult: CartDiscountCodesUpdateMutation | CheckoutMutation): mutationResult is CheckoutMutation {
        return 'manageCheckout' in mutationResult;
    }

    /**
     * @throws {Error}
     */
    async function processPromise({
        promise,
        processingStatus = 'busy',
        trackChanges = false,
        componentOrigin,
        customChangesHook,
    }: CartMutationsInterfaceBase & {
        promise: Promise<MutateResult<CartDiscountCodesUpdateMutation | CheckoutMutation>>;
    }) {
        cartStatus.value = processingStatus;
        try {
            const response = await promise;
            const responseData = response?.data;
            if (!responseData) return;

            const {
                checkout: checkoutData,
                userErrors = [],
                cart,
            } = (isCheckoutMutation(responseData) ? responseData.manageCheckout : responseData.cartDiscountCodesUpdate) || {};

            if (checkoutData) {
                if (trackChanges && import.meta.client) {
                    if (customChangesHook) {
                        customChangesHook(checkoutData);
                    } else {
                        const changes = getChanges(checkoutData);
                        if (changes.add?.length) nuxtApp.$gtm.addCartItems(changes.add, checkoutData, componentOrigin);
                        else if (changes.remove?.length) nuxtApp.$gtm.removeCartItems(changes.remove);
                    }
                }
                checkout.value = checkoutData;
                setCartItems();
                nuxtApp.$gtm.updateCart(checkoutData);
            }

            if (cart?.discountCodes) discountCodes.value = cart.discountCodes.map(({ code }) => code);

            if (userErrors.length > 0) {
                // console.log('user error on cart', userErrors);
                throw new Error(`got user error`, { cause: userErrors });
            }
        } catch (err) {
            cartStatus.value = 'error';
            // console.log('got mutation error', err);
            throw err;
        }
        while (initCallbacks.length > 0) {
            const callback = initCallbacks.pop();
            callback?.();
        }
    }

    function setCartItems() {
        // first run items with prices and discounts
        const lineItems = checkout.value?.lineItems ?? [];
        const currency = getCurrency();
        const priceFormatter = new Intl.NumberFormat(isoLocale.value, { style: 'currency', currency });
        const items: CartItem[] = lineItems.map((item) => {
            // calculate the discounted price
            const itemPrice = item.variant?.price?.amount ? Number.parseFloat(item.variant?.price?.amount) : 0;
            const quantity = item.quantity || 1;
            const quantityPrice = itemPrice * quantity;
            let totalDiscount = 0;
            if (item.discountAllocations) {
                for (const discount of item.discountAllocations) {
                    const disc = discount.discountedAmount?.amount ? Number.parseFloat(discount.discountedAmount.amount) : 0;
                    totalDiscount += disc;
                }
            }
            const discountedPrice = quantityPrice - totalDiscount;

            const attributes: Record<string, number | string> = {};
            for (const it of item.customAttributes) {
                if (['_limit'].includes(it.key)) {
                    attributes[it.key] = Number.parseInt(it.value);
                } else {
                    attributes[it.key] = it.value;
                }
            }
            const maxQuantity = (attributes._limit as number) || 0;

            const priceString = priceFormatter.format(discountedPrice);
            const compareAtPriceString = priceFormatter.format(quantityPrice);

            return {
                ...item,
                /**
                 * id is fixed to be string (TODO: check provided type by API)
                 */
                id: item.id as string,
                type: 'Product',
                quantity,
                shopTitle: item.shopTitle?.replace(': Default Title', '') ?? '',
                totalDiscount,
                quantityPrice,
                discountedPrice,
                image: item.variant?.shopImage,
                maxQuantity,
                attributes,
                priceString,
                compareAtPriceString: priceString === compareAtPriceString ? undefined : compareAtPriceString,
            };
        });
        cartItems.value = items;
        cartStatus.value = 'ready';
    }

    /**
     * @throws {Error}
     */
    async function mutationHandler({
        variables,
        processingStatus,
        trackChanges = false,
        componentOrigin,
        customChangesHook,
    }: CartMutationsInterfaceBase & {
        variables: CheckoutMutationVariables;
    }) {
        const promise = $fetch<MutateResult<CheckoutMutation>>('/api/checkout', {
            method: 'POST',
            body: JSON.stringify(variables),
        });
        return processPromise({ promise, processingStatus, trackChanges, componentOrigin, customChangesHook });
    }

    /**
     * @throws {Error}
     */
    async function updateDiscounts(variables: CartDiscountCodesUpdateMutationVariables) {
        if (discountCodes.value.length) {
            variables.discountCodes = [...arrayify(variables.discountCodes).filter(isDefined), ...discountCodes.value];
        }
        const promise = $fetch<MutateResult<CartDiscountCodesUpdateMutation>>('/api/discount-codes-update', {
            method: 'POST',
            body: JSON.stringify(variables),
        });
        return processPromise({ promise, trackChanges: false });
    }

    return {
        cartItems,
        cartRecommendations,
        cartStatus,
        checkout,
        totalItems,
        cartReadyHook,
        fetch,
        getComparePriceFormated,
        getPriceFormated,
        isLimitReached,
        loadCartCollection,
        mutationHandler,
        updateDiscounts,
    };
});

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useCartStore, import.meta.hot));
}
